import { useCallback, useContext, useId } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterRewardSeatsInfoModal from 'components/Flights/CharterBundle/MaldivesCharterRewardSeatsInfoModal'
import Clickable from 'components/Common/Clickable'
import Switch from 'LuxKit/Switch/Switch'
import { FILTER_CHIP_HEIGHTS } from 'LuxKit/Chips/FilterChip'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${rem(8)};
  /* Pin the icon size so the Switch's checkmark keeps its intrinsic size instead of inheriting the
     surrounding text's em - when this pill sits inside a modal-header Heading, an unset
     --text-icon-size falls back to 1em (the heading font size) and blows the tick up. */
  --text-icon-size: ${rem(20)};
  height: ${rem(FILTER_CHIP_HEIGHTS.medium)};
  padding: 0 ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  white-space: nowrap;
`

interface Props {
  checked: boolean
  onChange: (checked: boolean) => void
}

function MaldivesCharterRewardSeatsToggle({ checked, onChange }: Props) {
  const intl = useIntl()
  const switchId = useId()

  const showModal = useContext(ModalContext)
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }, [onChange])
  const handleInfoClick = useCallback(() => {
    showModal(<MaldivesCharterRewardSeatsInfoModal />)
  }, [showModal])

  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const label = intl.formatMessage({
    id: 'j122RKdmsQ7-Foht9OPf',
    defaultMessage: '{loyaltyProgramName} Rewards',
    description: 'Label beside the toggle in the Maldives charter calendar and “Select a class” modal; when on, non-reward fare cards are hidden so customers compare reward cabins only.',
  }, { loyaltyProgramName })
  const tooltip = intl.formatMessage({
    id: 'aPOI9gEqQeljyhwI1d40',
    defaultMessage: 'Turn this on to show only {loyaltyProgramName} Rewards fare classes, so you can compare reward seat options in one place.',
    description: 'Tooltip for the information icon next to the Société Rewards filter toggle in the charter fare class modal.',
  }, { loyaltyProgramName })
  const ariaLabel = intl.formatMessage({
    id: 'vcNEE7Ug6dL8qbzvJ7oi',
    defaultMessage: 'Show only {loyaltyProgramName} Rewards',
    description: 'Accessible name for the Société Rewards filter switch in the charter fare class modal.',
  }, { loyaltyProgramName })

  return (
    <Pill>
      <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
      <BodyText variant="small" weight="bold" colour="highlight-primary-dark" wrap="no-wrap">
        {label}
      </BodyText>
      <Clickable onClick={handleInfoClick} aria-label={tooltip}>
        <LineInfoCircleIcon colour="neutral-two" size={16} />
      </Clickable>
      <Switch
        id={switchId}
        checked={checked}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
    </Pill>
  )
}

export default MaldivesCharterRewardSeatsToggle
